import styled from "styled-components";
import { font } from "../../styles/typography";

// ── Page wrapper ──
const HomeWrapper = styled.div`
  min-height: calc(100vh - 60px);
  background: var(--spark-color-page-muted);
`;

// ── Hero / SPARK branding ──
const HeroSection = styled.div`
  text-align: center;
  padding: 20px 0 40px;
`;

const SparkLogo = styled.h1`
  ${font('hero')}
  font-weight: 800;
  color: var(--spark-color-brand);
  margin: 0 0 8px;
  letter-spacing: 6px;
  line-height: 1;
`;

const SparkExpansion = styled.p`
  ${font('xl')}
  color: var(--spark-color-text-muted);
  margin: 0 0 12px;
  font-style: italic;
  letter-spacing: 0.5px;
`;

const SparkDescription = styled.p`
  font-size: 20px;
  color: var(--spark-color-text-secondary);
  margin: 0 0 6px;
  font-weight: 500;
`;

const SparkTagline = styled.p`
  ${font('base')}
  color: var(--spark-color-text-muted);
  margin: 0;
`;

// ── Welcome banner (authenticated) ──
const WelcomeBanner = styled.div`
  background: linear-gradient(135deg, #e67e22, #f0a030);
  color: var(--spark-color-on-brand);
  border-radius: 12px;
  padding: 28px 36px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const WelcomeBannerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Greeting = styled.h2`
  margin: 0;
  ${font('pageTitle')}
  font-weight: 600;
`;

const UserBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 16px;
  border-radius: 20px;
  ${font('base')}
  font-weight: 500;
`;

// ── Section title ──
const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--spark-color-text);
  margin: 0 0 20px;
`;

// ── Apps grid ──
const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
  gap: 16px;
`;

const AppCard = styled.div`
  background: var(--spark-color-surface);
  border: 1px solid var(--spark-color-border);
  border-radius: 10px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--spark-color-brand);
    box-shadow: 0 4px 16px var(--spark-color-shadow-strong);
    transform: translateY(-2px);
  }
`;

const AppIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
  line-height: 1;
`;

const AppName = styled.h3`
  margin: 0 0 6px;
  ${font('xl')}
  font-weight: 600;
  color: var(--spark-color-text);
`;

const AppDescription = styled.p`
  margin: 0;
  ${font('md')}
  color: var(--spark-color-text-muted);
  line-height: 1.4;
`;

// ── Value propositions (unauthenticated) ──
const ValuePropsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(240px, 100%), 1fr));
  gap: 16px;
  margin-bottom: 40px;
`;

const PropCard = styled.div`
  background: var(--spark-color-surface);
  border: 1px solid var(--spark-color-border);
  border-radius: 10px;
  padding: 24px;
  text-align: center;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px var(--spark-color-shadow);
  }
`;

const PropIcon = styled.div`
  font-size: 36px;
  margin-bottom: 12px;
  line-height: 1;
`;

const PropTitle = styled.h3`
  margin: 0 0 8px;
  ${font('lg')}
  font-weight: 600;
  color: var(--spark-color-text);
`;

const PropText = styled.p`
  margin: 0;
  ${font('md')}
  color: var(--spark-color-text-muted);
  line-height: 1.4;
`;

// ── Login CTA ──
const LoginCta = styled.a`
  display: inline-block;
  padding: 14px 48px;
  background: var(--spark-color-brand);
  color: var(--spark-color-on-brand);
  ${font('xl')}
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
  cursor: pointer;
  border: none;

  &:hover {
    background: var(--spark-color-brand);
    filter: brightness(0.92);
  }
`;

// ── Download CTA (компактна, розміщується поруч із привітанням/теглайном) ──
const DownloadCta = styled.a`
  display: inline-block;
  flex-shrink: 0;
  padding: 10px 20px;
  background: var(--spark-color-surface);
  color: var(--spark-color-brand);
  ${font('base')}
  font-weight: 600;
  text-align: center;
  border: 2px solid var(--spark-color-brand);
  border-radius: 8px;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;
  cursor: pointer;

  &:hover {
    background: var(--spark-color-brand);
    color: var(--spark-color-on-brand);
  }
`;

const PwaInstallHint = styled.p`
  flex-basis: 100%;
  margin: 0;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  ${font('base')}
  line-height: 1.4;
`;

// ── States ──
const LoadingState = styled.div`
  text-align: center;
  padding: 80px 0;
  ${font('xl')}
  color: var(--spark-color-text-muted);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  ${font('lg')}
  color: var(--spark-color-text-muted);
  line-height: 1.6;
`;

export {
  HomeWrapper,
  HeroSection,
  SparkLogo,
  SparkExpansion,
  SparkDescription,
  SparkTagline,
  WelcomeBanner,
  WelcomeBannerInfo,
  Greeting,
  UserBadge,
  SectionTitle,
  AppsGrid,
  AppCard,
  AppIcon,
  AppName,
  AppDescription,
  ValuePropsGrid,
  PropCard,
  PropIcon,
  PropTitle,
  PropText,
  LoginCta,
  DownloadCta,
  PwaInstallHint,
  LoadingState,
  EmptyState,
};
